// frontend/src/InventoryKnowledge/InventoryKnowledge.jsx — /inventory-knowledge: plain-English guidance for the inventory tools
// (All guidance, Feedback, History tabs) and one entry's page at /inventory-knowledge/entries/:id.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SearchIcon } from '@shopify/polaris-icons';
import { Ic } from '../ui';
import { Alert, Modal } from '../ItAssets/common';
import { useList } from '../GiftCards/common';
import { imkApi } from './apiClient';
import { EntryForm, TemplatePicker } from './EntryForm';
import { STATUS, STRENGTH, StatusOf, StrengthPill, TOOLS, ToolChips, Verdict, day, lineText, when } from './common';

const TABS = [['all', 'All guidance'], ['feedback', 'Feedback'], ['history', 'History']];

export default function InventoryKnowledge() {
  const { tab = 'all', id } = useParams();
  const navigate = useNavigate();
  const [options, setOptions] = useState(null);
  const [dialog, setDialog] = useState(null);           // {kind: 'pick'} | {kind: 'form', template, entry, seed, fromFeedbackId}
  const list = useList(() => imkApi.entries(), []);
  useEffect(() => { imkApi.options().then(setOptions).catch(() => setOptions({})); }, []);
  const saved = (newId) => { setDialog(null); list.reload(); navigate(`/inventory-knowledge/entries/${newId}`); };
  const newCount = list.data?.feedback?.new || 0;
  return (
    <section className="uh-page uh-imk">
      <div className="uh-ph">
        <div>
          <h1 className="uh-h1">Inventory Movement Knowledge</h1>
          <div className="uh-sub">How we want stock to move, in plain English. Each entry says WHEN a situation comes up and what to DO. The rebalancing tools, and later B2B order recommendations, build their rules from it.</div>
        </div>
        <span className="uh-sp" />
        <button type="button" className="uh-btn primary" onClick={() => setDialog({ kind: 'pick' })}>Add guidance</button>
      </div>
      {!id && (
        <div className="uh-ia-tabs" role="tablist">
          {TABS.map(([k, label]) => <button key={k} type="button" role="tab" aria-selected={tab === k} className="uh-ia-tab"
            onClick={() => navigate(k === 'all' ? '/inventory-knowledge' : `/inventory-knowledge/${k}`)}>{label}{k === 'feedback' && newCount > 0 && <span className="uh-imk-n">{newCount}</span>}</button>)}
        </div>
      )}
      <Alert>{list.error}</Alert>
      {id ? <EntryPage id={id} onEdit={(entry) => setDialog({ kind: 'form', entry })} reloadKey={list.data} />
        : tab === 'feedback' ? <FeedbackTab entries={list.data?.entries || []} onChanged={list.reload}
          onMake={(f) => setDialog({ kind: 'form', fromFeedbackId: f.id, seed: seedFrom(f) })} />
          : tab === 'history' ? <HistoryTab /> : <ListTab data={list.data} />}
      {dialog?.kind === 'pick' && <TemplatePicker onClose={() => setDialog(null)} onPick={(t) => setDialog({ kind: 'form', template: t })} />}
      {dialog?.kind === 'form' && <EntryForm {...dialog} options={options} onClose={() => setDialog(null)} onSaved={saved} />}
    </section>
  );
}

// '(NIKE-FQ1456-446) Nike Mercurial Superfly 10 Academy FG [RACER BLUE/WHITE] 6.5' -> 'Nike Mercurial Superfly 10 Academy FG'
const styleName = (f) => (f.item_name || f.sku).replace(/^\([^)]*\)\s*/, '').replace(/\s*\[.*$/, '');
const seedFrom = (f) => ({
  title: `WHEN ${f.dest_name || 'the warehouse'} receives ${styleName(f)}`, tools: [f.strategy.includes('consolidate') ? 'consolidation' : 'rebalancing'],
  strength: f.verdict === 'agree' ? 'prefer' : 'avoid', scope: { stores: f.dest_name ? [f.dest_name] : [], styles: [styleName(f)] },
  body: `WHEN a recommendation like ${lineText(f)}\n\n${f.verdict === 'agree' ? 'DO' : "DON'T"}\n- \n\nWHY\n${f.reason}`,
  example: `${lineText(f)} (report #${f.report_id}): ${f.verdict === 'agree' ? 'right' : 'wrong'}. ${f.reason}`,
});

function ListTab({ data }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [tool, setTool] = useState('');
  const [strength, setStrength] = useState('');
  const [status, setStatus] = useState('');
  const rows = useMemo(() => (data?.entries || []).filter((e) => {
    const t = q.trim().toLowerCase();
    return (!t || `${e.title} ${e.scope_text} ${e.body}`.toLowerCase().includes(t)) && (!tool || e.tools.includes(tool))
      && (!strength || e.strength === strength) && (!status || e.shown_status === status);
  }), [data, q, tool, strength, status]);
  return (
    <div className="uh-card">
      <div className="uh-ir-tbar">
        <div className="uh-search" style={{ flex: '1 1 260px', maxWidth: 380 }}><Ic source={SearchIcon} sm />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Guidance, store, category" aria-label="Search guidance" /></div>
        <select className="uh-inp uh-imk-sel" aria-label="Tool" value={tool} onChange={(e) => setTool(e.target.value)}>
          <option value="">Every tool</option>{Object.entries(TOOLS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
        <select className="uh-inp uh-imk-sel" aria-label="Strength" value={strength} onChange={(e) => setStrength(e.target.value)}>
          <option value="">Any strength</option>{Object.entries(STRENGTH).map(([k, [v]]) => <option key={k} value={k}>{v}</option>)}</select>
        <select className="uh-inp uh-imk-sel" aria-label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Any status</option>{Object.entries(STATUS).map(([k, [v]]) => <option key={k} value={k}>{v}</option>)}</select>
        <span className="uh-sp" /><span className="uh-muted">{rows.length} of {(data?.entries || []).length}</span>
      </div>
      <div className="uh-tscroll"><table className="uh-tbl">
        <thead><tr><th>Guidance</th><th>Used by</th><th>Strength</th><th>Status</th><th className="r">Feedback</th><th>Updated</th></tr></thead>
        <tbody>
          {rows.map((e) => (
            <tr key={e.id} className="uh-row-link" onClick={() => navigate(`/inventory-knowledge/entries/${e.id}`)}>
              <td><div className="nm uh-imk-t">{e.title}</div><div className="uh-muted uh-imk-sm">{e.scope_text}{e.ends_on ? ` · ends ${day(e.ends_on)}` : ''}</div></td>
              <td><ToolChips tools={e.tools} /></td><td><StrengthPill s={e.strength} /></td><td><StatusOf s={e.shown_status} /></td>
              <td className="r uh-num uh-ia-nowrap">{Number(e.agree) + Number(e.disagree) ? <><span className="uh-imk-ok">{e.agree} agree</span> · <span className="uh-imk-bad">{e.disagree} disagree</span></> : <span className="uh-faint">—</span>}</td>
              <td className="uh-ia-nowrap"><div>{day(e.updated_at)}</div><div className="uh-muted uh-imk-sm">{e.updated_by_name}</div></td>
            </tr>))}
          {data && !rows.length && <tr><td colSpan={6}><div className="uh-empty">{data.entries.length ? 'No guidance matches. Clear a filter.' : 'No guidance yet. Choose Add guidance to write the first entry.'}</div></td></tr>}
        </tbody></table></div>
    </div>
  );
}

function EntryPage({ id, onEdit, reloadKey }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const load = () => imkApi.entry(id).then((d) => { setError(''); setData(d); }).catch((e) => setError(e.message));
  useEffect(() => { load(); }, [id, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps
  if (error) return <Alert>{error}</Alert>;
  if (!data) return <div className="uh-card"><div className="uh-empty">Loading…</div></div>;
  const e = data.entry;
  const flip = async (status) => { try { await imkApi.setStatus(e.id, status); load(); } catch (err) { setError(err.message); } };
  const sc = (k) => (e.scope[k] || []).length ? e.scope[k].map((x) => <span key={x} className="uh-ia-tag uh-imk-chip">{x}</span>) : <span className="uh-faint">All</span>;
  return (
    <>
      <div className="uh-imk-crumb"><button type="button" className="uh-link" onClick={() => navigate('/inventory-knowledge')}>All guidance</button> › {e.title}</div>
      <div className="uh-imk-entry">
        <article className="doc">
          <div className="uh-imk-acts">
            <h2 className="uh-h2 lg">{e.title}</h2><span className="uh-sp" />
            <button type="button" className="uh-btn slim" onClick={() => onEdit(e)}>Edit</button>
            {e.status === 'published' && <button type="button" className="uh-btn slim" onClick={() => flip('off')}>Turn off</button>}
            {e.status !== 'published' && <button type="button" className="uh-btn slim primary" onClick={() => flip('published')}>Publish</button>}
          </div>
          <div className="uh-imk-text">{e.body || <span className="uh-faint">Nothing written yet.</span>}</div>
          {e.example && <div className="uh-imk-ex"><b>Example the rules must get right</b><div>{e.example}</div></div>}
        </article>
        <aside className="rail">
          <div className="sec"><h3>Details</h3><dl className="uh-imk-kv">
            <dt>Status</dt><dd><StatusOf s={e.shown_status} /></dd>
            <dt>Strength</dt><dd><StrengthPill s={e.strength} /></dd>
            <dt>Used by</dt><dd><ToolChips tools={e.tools} /></dd>
            <dt>Categories</dt><dd>{sc('categories')}</dd><dt>Stores</dt><dd>{sc('stores')}</dd>
            <dt>Price tiers</dt><dd>{sc('price_tiers')}</dd><dt>Vendors</dt><dd>{sc('vendors')}</dd><dt>Styles</dt><dd>{sc('styles')}</dd>
            <dt>Dates</dt><dd>{e.starts_on || e.ends_on ? `${e.starts_on ? day(e.starts_on) : 'Now'} to ${e.ends_on ? day(e.ends_on) : 'no end'}` : 'Always'}</dd>
            <dt>Written by</dt><dd>{e.created_by_name}, {day(e.created_at)}</dd>
            <dt>Last change</dt><dd>{e.updated_by_name}, {when(e.updated_at)}</dd>
          </dl></div>
          <div className="sec"><h3>Feedback linked here <span className="uh-muted">({data.feedback.length})</span></h3>
            {data.feedback.length ? data.feedback.map((f) => (
              <div key={f.id} className="uh-imk-fb"><Verdict v={f.verdict} /> <b>{lineText(f)}</b>
                <div className="uh-imk-sm uh-muted">{f.by_name}, {when(f.created_at)} · report #{f.report_id}</div><div>“{f.reason}”</div></div>))
              : <div className="uh-muted">None yet. Link feedback from the Feedback tab.</div>}</div>
          <div className="sec"><h3>Changes</h3>
            {data.versions.map((v) => <div key={v.id} className="uh-imk-sm"><span className="uh-muted">{when(v.changed_at)}</span> {v.by_name}: {v.what}</div>)}</div>
        </aside>
      </div>
    </>
  );
}

function FeedbackTab({ entries, onChanged, onMake }) {
  const navigate = useNavigate();
  const [state, setState] = useState('new');
  const fb = useList(() => imkApi.feedback(state), [state]);
  const [linking, setLinking] = useState(null);
  const [error, setError] = useState('');
  const act = async (f, action, entryId) => { try { await imkApi.handle(f.id, action, entryId); setLinking(null); fb.reload(); onChanged(); } catch (e) { setError(e.message); } };
  const rows = fb.data?.feedback || [];
  return (
    <div className="uh-card">
      <div className="uh-ir-tbar">
        {[['new', 'New'], ['linked', 'Linked to guidance'], ['dismissed', 'Dismissed'], ['', 'All']].map(([k, label]) => (
          <button key={k} type="button" className="uh-ir-pill" aria-pressed={state === k} onClick={() => setState(k)}>{label}</button>))}
        <span className="uh-sp" /><span className="uh-muted">Every agree and disagree given on a report line, with the reason the person typed.</span>
      </div>
      <Alert>{error || fb.error}</Alert>
      <div className="uh-tscroll"><table className="uh-tbl">
        <thead><tr><th>Recommendation</th><th>Verdict</th><th>Reason</th><th>From</th><th>Status</th><th /></tr></thead>
        <tbody>
          {rows.map((f) => (
            <tr key={f.id}>
              <td><div className="nm">{f.src_name} → {f.dest_name || 'Warehouse'}</div><div className="uh-muted uh-imk-sm">{f.qty} × {f.item_name || f.sku} · report #{f.report_id}{f.strategy ? ` · ${f.strategy.replace('_', ' ')}` : ''}</div>
                {f.why && <div className="uh-faint uh-imk-sm">Why it was suggested: {f.why}</div>}</td>
              <td><Verdict v={f.verdict} /></td>
              <td className="uh-imk-reason">{f.reason}</td>
              <td className="uh-ia-nowrap">{f.by_name}<div className="uh-muted uh-imk-sm">{when(f.created_at)}</div></td>
              <td>{f.state === 'linked' ? <button type="button" className="uh-link" onClick={() => navigate(`/inventory-knowledge/entries/${f.entry_id}`)}>{f.entry_title}</button> : f.state === 'dismissed' ? <span className="uh-muted">Dismissed by {f.handled_by_name}</span> : <span className="uh-badge uh-s-par"><span className="d" />New</span>}</td>
              <td className="r uh-ia-nowrap">
                {f.state === 'new' ? <>
                  <button type="button" className="uh-btn slim" onClick={() => onMake(f)}>Make guidance</button>{' '}
                  <button type="button" className="uh-btn slim" disabled={!entries.length} onClick={() => setLinking(f)}>Add to guidance</button>{' '}
                  <button type="button" className="uh-btn slim" onClick={() => act(f, 'dismiss')}>Dismiss</button></>
                  : <button type="button" className="uh-btn slim" onClick={() => act(f, 'reopen')}>Reopen</button>}
              </td>
            </tr>))}
          {fb.data && !rows.length && <tr><td colSpan={6}><div className="uh-empty">{state === 'new' ? 'No new feedback. People give it with the agree and disagree buttons on the Inventory Rebalancing V2 tab.' : 'Nothing here.'}</div></td></tr>}
        </tbody></table></div>
      {linking && <LinkDialog f={linking} entries={entries} onClose={() => setLinking(null)} onLink={(eid) => act(linking, 'link', eid)} />}
    </div>
  );
}

function LinkDialog({ f, entries, onClose, onLink }) {
  const [pick, setPick] = useState('');
  return (
    <Modal title="Add feedback to guidance" subtitle={lineText(f)} onClose={onClose}
      footer={<><span className="uh-sp" /><button type="button" className="uh-btn" onClick={onClose}>Cancel</button>
        <button type="button" className="uh-btn primary" disabled={!pick} onClick={() => onLink(Number(pick))}>Add</button></>}>
      <p>“{f.reason}”</p>
      <select className="uh-inp" aria-label="Guidance" value={pick} onChange={(e) => setPick(e.target.value)}>
        <option value="">Choose the guidance it belongs to</option>
        {entries.map((e) => <option key={e.id} value={e.id}>{e.title}</option>)}</select>
      <div className="uh-ia-hint" style={{ marginTop: 6 }}>Edit the guidance afterwards if the feedback changes what it should say.</div>
    </Modal>
  );
}

function HistoryTab() {
  const navigate = useNavigate();
  const { data, error } = useList(() => imkApi.history(), []);
  return (
    <div className="uh-card">
      <Alert>{error}</Alert>
      <div className="uh-tscroll"><table className="uh-tbl">
        <thead><tr><th>When</th><th>Who</th><th>What happened</th></tr></thead>
        <tbody>
          {(data?.history || []).map((v) => (
            <tr key={v.id} className="uh-row-link" onClick={() => navigate(`/inventory-knowledge/entries/${v.entry_id}`)}>
              <td className="uh-ia-nowrap">{when(v.changed_at)}</td><td className="uh-ia-nowrap">{v.by_name}</td><td>{v.what}</td></tr>))}
          {data && !data.history.length && <tr><td colSpan={3}><div className="uh-empty">Nothing yet. Every guidance change is listed here.</div></td></tr>}
        </tbody></table></div>
    </div>
  );
}
